import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import { usePublishEventTabs } from '../context/EventShellContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ArrowLeft, Loader2, Users, FileText, Trophy, Play, Settings, Pencil, BarChart3, Star, Eye, EyeOff, Award, Download, X, MessageSquare, Check as CheckIcon, Sparkles, Search, LayoutGrid, List as ListIcon, UserPlus, Copy, Calendar as CalendarIcon, Plus, FlaskConical, ListOrdered } from 'lucide-react';

interface EventDetail {
  eventId: string;
  name: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  submissionDeadline: string;
  themes: Array<{ id: string; name: string; description: string }>;
  scoringCriteria: Array<{ name: string; weight: number; maxScore: number }>;
  settings: { minTeamSize: number; maxTeamSize: number; timerDurationSeconds: number; allowSelfJoin?: boolean };
}

interface TeamItem {
  teamId: string;
  name: string;
  themeId: string | null;
  members: Array<{ sub: string; name: string; role: string }>;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  submission_locked: 'bg-orange-100 text-orange-700',
  live_demo: 'bg-purple-100 text-purple-700',
  judging: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  archived: 'bg-gray-200 text-gray-600',
};

const ARCHIVE_ACTION = { action: 'archive', label: 'Archive', style: 'btn-ghost', destructive: true, confirm: 'This will archive the event. You can unarchive it later.' };

const TRANSITIONS: Record<string, Array<{ action: string; label: string; style: string; destructive?: boolean; confirm?: string }>> = {
  draft: [{ action: 'publish', label: 'Publish Event', style: 'btn-primary' }, ARCHIVE_ACTION],
  open: [
    { action: 'start', label: 'Start Hacking', style: 'btn-primary' },
    { action: 'unpublish', label: 'Unpublish', style: 'btn-ghost', destructive: true, confirm: 'This will hide the event from participants. Are you sure?' },
    ARCHIVE_ACTION,
  ],
  in_progress: [
    { action: 'lock_submissions', label: 'Lock Submissions', style: 'btn-primary', destructive: true, confirm: 'This will prevent all further submission edits. This cannot be undone.' },
    ARCHIVE_ACTION,
  ],
  submission_locked: [{ action: 'start_live', label: 'Start Live Demo', style: 'btn-primary' }, ARCHIVE_ACTION],
  live_demo: [{ action: 'end_live', label: 'End Live Demo', style: 'btn-primary', destructive: true, confirm: 'This will end the live presentation session and move to judging.' }],
  judging: [{ action: 'finalize', label: 'Finalize Results', style: 'btn-primary', destructive: true, confirm: 'This will freeze all scores and publish the leaderboard. This cannot be undone.' }],
  completed: [ARCHIVE_ACTION],
  archived: [{ action: 'unarchive', label: 'Unarchive', style: 'btn-secondary' }],
};

type Tab = 'overview' | 'teams' | 'scoring' | 'leaderboard' | 'live' | 'feedback' | 'settings';

const VALID_TABS: Tab[] = ['overview', 'teams', 'scoring', 'leaderboard', 'live', 'feedback', 'settings'];
const DEFAULT_TAB: Tab = 'overview';
const TAB_PARAM = 'tab';

export function EventDetailPage() {
  const { eventId } = useParams();
  const { can, user } = useAuth();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [myTeam, setMyTeam] = useState<TeamItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  // Tab is synced with the ?tab= URL param so links are shareable and
  // browser back/forward works across tabs.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get(TAB_PARAM);
  const activeTab: Tab = (VALID_TABS as string[]).includes(rawTab ?? '')
    ? (rawTab as Tab)
    : DEFAULT_TAB;
  const setActiveTab = useCallback(
    (next: Tab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === DEFAULT_TAB) {
            params.delete(TAB_PARAM);
          } else {
            params.set(TAB_PARAM, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [confirmAction, setConfirmAction] = useState<{ action: string; message: string } | null>(null);

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: EventDetail }>(`/api/events/${eventId}`),
      networkManager.get<{ status: boolean; data: TeamItem[] }>(`/api/events/${eventId}/teams`).catch(() => ({ data: [] })),
    ])
      .then(([eventRes, teamsRes]) => {
        setEvent(eventRes.data);
        const allTeams = teamsRes.data as TeamItem[];
        setTeams(allTeams);
        // Find if current user is on a team
        if (user) {
          const found = allTeams.find((t) =>
            t.members?.some((m: { sub: string }) => m.sub === user.sub),
          );
          setMyTeam(found ?? null);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [eventId]);

  async function handleTransition(action: string) {
    if (!eventId) return;
    setTransitioning(true);
    setConfirmAction(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: { status: string } }>(
        `/api/events/${eventId}/transition`,
        { action },
      );
      setEvent((prev) => (prev ? { ...prev, status: res.data.status } : null));
      setTransitionError(null);
    } catch (err) {
      setTransitionError(getErrorMessage(err, 'Transition failed'));
    } finally {
      setTransitioning(false);
    }
  }

  const tabs = useMemo(() => {
    if (!event) return [] as Array<{ id: Tab; label: string; icon: typeof Users }>;
    const eventRecord = event as unknown as Record<string, unknown>;
    const feedbackEnabled = !!(eventRecord?.feedbackForm && (eventRecord.feedbackForm as { enabled?: boolean }).enabled);
    // Run-of-Show / Live tab: single entry point that adapts to the event lifecycle.
    // - open/in_progress/submission_locked → "Run of Show" (organizers only — plan ahead)
    // - live_demo → "Live" (everyone, mid-session UX)
    const showLiveTab = event.status === 'live_demo';
    const showRunOfShowTab =
      ['open', 'in_progress', 'submission_locked'].includes(event.status) && can('live:control');
    const liveOrPlanTab: Array<{ id: Tab; label: string; icon: typeof Users }> = showLiveTab
      ? [{ id: 'live', label: 'Live', icon: Play }]
      : showRunOfShowTab
      ? [{ id: 'live', label: 'Run of Show', icon: ListOrdered }]
      : [];
    const list: Array<{ id: Tab; label: string; icon: typeof Users }> = [
      { id: 'overview', label: 'Overview', icon: FileText },
      { id: 'teams', label: `Teams (${teams.length})`, icon: Users },
      { id: 'scoring', label: 'Scoring', icon: Trophy },
      { id: 'leaderboard', label: 'Leaderboard', icon: BarChart3 },
      ...liveOrPlanTab,
      ...(feedbackEnabled ? [{ id: 'feedback' as Tab, label: 'Feedback', icon: MessageSquare }] : []),
      ...(can('event:edit') ? [{ id: 'settings' as Tab, label: 'Settings', icon: Settings }] : []),
    ];
    return list;
  }, [event, teams.length, can]);

  // Publish tabs into the shell so mobile bottom nav can render them.
  usePublishEventTabs(tabs);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!event) {
    return <p className="py-12 text-center text-ink-secondary">Event not found.</p>;
  }

  const actions = can('event:transition') ? TRANSITIONS[event.status] ?? [] : [];
  const isJoinableState = ['open', 'in_progress'].includes(event.status);
  const allowSelfJoin = event.settings.allowSelfJoin !== false;
  const canJoin = isJoinableState && can('team:create') && !myTeam && allowSelfJoin;

  return (
    <div className="animate-fade-in">
      <Link to="/" className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      {/* Error banner */}
      {transitionError && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between">
          <span>{transitionError}</span>
          <button className="text-xs text-red-600 underline ml-3" onClick={() => setTransitionError(null)}>Dismiss</button>
        </div>
      )}

      {/* Cover image banner */}
      {(event as unknown as { coverImageUrl?: string }).coverImageUrl && (
        <div className="mb-4 aspect-[4/1] overflow-hidden rounded-xl border border-ring bg-gray-100 sm:aspect-[6/1]">
          <img
            src={(event as unknown as { coverImageUrl?: string }).coverImageUrl}
            alt={event.name}
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {/* Header */}
      <div className="space-y-3">
        {/* Title row with status and dates inline */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[event.status] ?? ''}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${event.status === 'live_demo' || event.status === 'judging' ? 'animate-pulse' : ''} ${STATUS_COLORS[event.status]?.includes('bg-') ? STATUS_COLORS[event.status].split(' ')[0].replace('bg-', 'bg-').replace('-100', '-500') : 'bg-gray-500'}`} />
                {event.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              {(event as unknown as { isTest?: boolean }).isTest && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-purple-700">
                  <FlaskConical className="h-3 w-3" /> Test event
                </span>
              )}
              {event.startDate && event.endDate && (
                <span className="inline-flex items-center gap-1 text-xs text-ink-tertiary">
                  <CalendarIcon className="h-3 w-3" />
                  {(() => {
                    const s = new Date(event.startDate);
                    const e = new Date(event.endDate);
                    const sameYear = s.getFullYear() === e.getFullYear();
                    const sameMonth = sameYear && s.getMonth() === e.getMonth();
                    if (sameMonth) return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}–${e.getDate()}, ${e.getFullYear()}`;
                    if (sameYear) return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${e.getFullYear()}`;
                    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  })()}
                </span>
              )}
            </div>
            <h1 className="mt-1.5 font-display text-2xl font-bold text-ink sm:text-3xl">{event.name}</h1>
            {event.description && (
              <p className="mt-2 max-w-3xl text-sm text-ink-secondary">{event.description}</p>
            )}
          </div>

          {/* Primary actions column */}
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {myTeam && (
              <Link to={`/events/${eventId}/teams/${myTeam.teamId}`} className="btn-primary inline-flex items-center gap-1.5 text-sm">
                <Users className="h-4 w-4" /> My Team
              </Link>
            )}
            {canJoin && (
              <Link to={`/events/${eventId}/teams/new`} className="btn-primary inline-flex items-center gap-1.5 text-sm">
                <UserPlus className="h-4 w-4" /> Join Event
              </Link>
            )}
            {event.status === 'live_demo' && can('live:control') && (
              <Link to={`/events/${eventId}/live`} className="btn-primary inline-flex items-center gap-1.5 text-sm">
                <Play className="h-4 w-4" /> Live Control
              </Link>
            )}
            {['submission_locked', 'live_demo', 'judging'].includes(event.status) && can('score:submit') && teams.length > 0 && (
              <Link to={`/events/${eventId}/judge/${teams[0].teamId}`} className="btn-primary inline-flex items-center gap-1.5 text-sm">
                <Trophy className="h-4 w-4" /> Start Judging
              </Link>
            )}
          </div>
        </div>

        {/* Secondary actions row — organizer tools */}
        {(can('event:edit') || can('event:create') || actions.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 border-t border-ring pt-3">
            {/* State transitions (non-destructive first, then destructive with distinct style) */}
            {actions.map((a) => {
              // Non-destructive transitions use a muted-emphasis style
              // Destructive transitions use an amber warning style (less alarming than red, more
              // obvious than ghost — they're intentional but should not dominate like btn-primary)
              const buttonClass = a.destructive
                ? 'inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50'
                : 'inline-flex items-center gap-1.5 rounded-lg border border-ring bg-white px-2.5 py-1.5 text-sm font-medium text-ink hover:bg-gray-50 transition-colors disabled:opacity-50';
              return (
                <button
                  key={a.action}
                  className={buttonClass}
                  onClick={() =>
                    a.destructive && a.confirm
                      ? setConfirmAction({ action: a.action, message: a.confirm })
                      : handleTransition(a.action)
                  }
                  disabled={transitioning}
                >
                  {transitioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  {a.label}
                </button>
              );
            })}
            {can('event:edit') && !['completed', 'archived'].includes(event.status) && (
              <Link to={`/events/${eventId}/edit`} className="btn-ghost text-sm inline-flex items-center gap-1">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            )}
            {can('event:create') && (
              <button
                className="btn-ghost text-sm inline-flex items-center gap-1"
                onClick={async () => {
                  try {
                    const res = await networkManager.post<{ status: boolean; data: { eventId: string } }>(`/api/events/${eventId}/clone`);
                    window.location.href = `/events/${res.data.eventId}`;
                  } catch (err) {
                    setTransitionError(getErrorMessage(err, 'Failed to clone event'));
                  }
                }}
              >
                <Copy className="h-3.5 w-3.5" /> Clone
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs — hidden on mobile; shell renders a bottom nav instead */}
      <div className="mt-6 hidden gap-1 overflow-x-auto border-b border-ring scrollbar-hide md:flex">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-secondary hover:text-ink'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'overview' && <OverviewTab event={event} />}
        {activeTab === 'teams' && <TeamsTab eventId={event.eventId} teams={teams} canJoin={canJoin} myTeam={myTeam} themes={event.themes} />}
        {activeTab === 'scoring' && <ScoringTab event={event} />}
        {activeTab === 'leaderboard' && <LeaderboardTab eventId={event.eventId} />}
        {activeTab === 'live' && event.status === 'live_demo' && (
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="font-semibold text-ink">Live Demo Day</h3>
              <p className="mt-1 text-sm text-ink-secondary">The event is in live demo mode. Use the tools below to manage or participate.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {can('live:control') && (
                  <Link to={`/events/${eventId}/live`} className="btn-primary inline-flex items-center gap-2">
                    <Play className="h-4 w-4" /> Control Panel
                  </Link>
                )}
                {can('score:submit') && (
                  <Link to={`/events/${eventId}/live/judge`} className="btn-secondary inline-flex items-center gap-2">
                    <Star className="h-4 w-4" /> Judge View
                  </Link>
                )}
                <a
                  href={`/events/${eventId}/live/display`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" /> Big Screen Display
                </a>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'live' && event.status !== 'live_demo' && can('live:control') && (
          <div className="space-y-4">
            <div className="card p-5">
              <div className="flex items-start gap-3">
                <ListOrdered className="mt-0.5 h-5 w-5 shrink-0 text-ink-tertiary" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-ink">Run of Show</h3>
                  <p className="mt-1 text-sm text-ink-secondary">
                    {event.status === 'submission_locked'
                      ? 'Submissions are locked. Lock in the demo order and start time — teams will see their slot on their dashboard.'
                      : event.status === 'in_progress'
                      ? 'Get a head start on demo day. Pick an order and a start time now — you can keep editing until the session begins.'
                      : 'Teams can still join, but you can already draft a demo order. It\'ll update as new teams arrive.'}
                  </p>
                  <p className="mt-2 text-xs text-ink-tertiary">
                    Saving the schedule publishes it to every team&apos;s dashboard immediately.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to={`/events/${eventId}/live`} className="btn-primary inline-flex items-center gap-2">
                  <ListOrdered className="h-4 w-4" /> Plan Run of Show
                </Link>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'feedback' && <EventFeedbackTab eventId={event.eventId} event={event} />}
        {activeTab === 'settings' && <SettingsTab event={event} />}
      </div>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title="Are you sure?"
        message={confirmAction?.message ?? ''}
        confirmLabel="Yes, proceed"
        onConfirm={() => confirmAction && handleTransition(confirmAction.action)}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function OverviewTab({ event }: { event: EventDetail }) {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-lg font-semibold text-ink">Themes</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {event.themes.map((theme) => (
            <div key={theme.id} className="card p-4">
              <h3 className="font-medium text-ink">{theme.name}</h3>
              <p className="mt-1 text-sm text-ink-secondary">{theme.description}</p>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="text-lg font-semibold text-ink">Key Dates</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div className="card p-4">
            <p className="text-sm text-ink-secondary">Start</p>
            <p className="font-semibold text-ink">{event.startDate ? new Date(event.startDate).toLocaleString() : '—'}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-ink-secondary">Submission Deadline</p>
            <p className="font-semibold text-ink">{event.submissionDeadline ? new Date(event.submissionDeadline).toLocaleString() : '—'}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-ink-secondary">End</p>
            <p className="font-semibold text-ink">{event.endDate ? new Date(event.endDate).toLocaleString() : '—'}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

// Theme chip color palette — consistent per theme across cards
const THEME_COLORS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-purple-50 text-purple-700 border-purple-200',
  'bg-green-50 text-green-700 border-green-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-pink-50 text-pink-700 border-pink-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
  'bg-indigo-50 text-indigo-700 border-indigo-200',
  'bg-rose-50 text-rose-700 border-rose-200',
];
function themeColor(themeId: string): string {
  let hash = 0;
  for (let i = 0; i < themeId.length; i++) hash = themeId.charCodeAt(i) + ((hash << 5) - hash);
  return THEME_COLORS[Math.abs(hash) % THEME_COLORS.length];
}

function TeamsTab({ eventId, teams, canJoin, myTeam, themes }: { eventId: string; teams: TeamItem[]; canJoin: boolean; myTeam: TeamItem | null; themes: EventDetail['themes'] }) {
  const { can, user } = useAuth();
  const canManageTeams = can('submission:view-all'); // admins + organizers

  // Search + sort + view mode
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'theme'>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [themeFilter, setThemeFilter] = useState<string>('all');

  // Admin inline create team
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamTheme, setNewTeamTheme] = useState(themes[0]?.id ?? '');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  async function handleAdminCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      await networkManager.post(`/api/events/${eventId}/teams/admin-create`, {
        name: newTeamName.trim(),
        themeId: newTeamTheme,
      });
      window.location.reload();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create team';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  }

  // Filter + sort teams
  const filteredTeams = useMemo(() => {
    const q = query.trim().toLowerCase();
    return teams
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .filter((t) => themeFilter === 'all' || t.themeId === themeFilter)
      .sort((a, b) => {
        if (sortBy === 'size') return b.members.length - a.members.length;
        if (sortBy === 'theme') {
          const aTheme = themes.find((th) => th.id === a.themeId)?.name ?? '';
          const bTheme = themes.find((th) => th.id === b.themeId)?.name ?? '';
          return aTheme.localeCompare(bTheme);
        }
        return a.name.localeCompare(b.name);
      });
  }, [teams, query, themeFilter, sortBy, themes]);

  /* ---------- Empty state ---------- */
  if (teams.length === 0) {
    return (
      <div className="card flex flex-col items-center py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-subtle">
          <Users className="h-7 w-7 text-ink-secondary" />
        </div>
        <p className="mt-4 font-semibold text-ink">No teams yet</p>
        <p className="mt-1 text-sm text-ink-secondary max-w-sm">
          {canJoin
            ? 'Be the first to create a team and invite others to join you.'
            : canManageTeams
              ? 'Teams will appear here once created. You can add the first team below.'
              : 'Check back soon when teams start forming.'}
        </p>
        <div className="mt-5 flex gap-2">
          {canJoin && (
            <>
              <Link to={`/events/${eventId}/teams/new`} className="btn-primary inline-flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> Create a Team
              </Link>
              <Link to={`/events/${eventId}/teams/join`} className="btn-secondary">Join with invite code</Link>
            </>
          )}
          {canManageTeams && !canJoin && !showCreateForm && (
            <button onClick={() => setShowCreateForm(true)} className="btn-primary inline-flex items-center gap-1.5">
              <Plus className="h-4 w-4" /> Add Team
            </button>
          )}
        </div>
        {showCreateForm && canManageTeams && (
          <form onSubmit={handleAdminCreateTeam} className="mt-5 w-full max-w-sm space-y-2 text-left">
            <input type="text" className="input-base" placeholder="Team name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} autoFocus />
            <select className="input-base" value={newTeamTheme} onChange={(e) => setNewTeamTheme(e.target.value)}>
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {createError && <p className="text-xs text-red-600">{createError}</p>}
            <div className="flex gap-2">
              <button type="button" className="btn-secondary text-sm flex-1" onClick={() => setShowCreateForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary text-sm flex-1 inline-flex items-center justify-center gap-1" disabled={creating || !newTeamName.trim()}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Create
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  /* ---------- Main list ---------- */
  return (
    <div>
      {/* My team banner */}
      {myTeam && (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50/50 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-sm font-bold text-white">
              {myTeam.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider text-blue-600">You're on</p>
              <p className="truncate text-sm font-semibold text-blue-900">{myTeam.name}</p>
            </div>
          </div>
          <Link to={`/events/${eventId}/teams/${myTeam.teamId}`} className="btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1">
            Go to my team <ArrowLeft className="h-3 w-3 rotate-180" />
          </Link>
        </div>
      )}

      {/* Toolbar: search, filter, sort, view, actions */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Search */}
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
          <input
            type="text"
            placeholder={`Search ${teams.length} team${teams.length !== 1 ? 's' : ''}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input-base pl-9 pr-9 text-sm"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-tertiary hover:bg-gray-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter / sort / view / actions */}
        <div className="flex flex-wrap items-center gap-2">
          {themes.length > 1 && (
            <select
              value={themeFilter}
              onChange={(e) => setThemeFilter(e.target.value)}
              className="rounded-lg border border-ring bg-white px-2.5 py-1.5 text-xs font-medium text-ink"
            >
              <option value="all">All themes</option>
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'size' | 'theme')}
            className="rounded-lg border border-ring bg-white px-2.5 py-1.5 text-xs font-medium text-ink"
          >
            <option value="name">Sort: Name</option>
            <option value="size">Sort: Team size</option>
            <option value="theme">Sort: Theme</option>
          </select>
          <div className="inline-flex rounded-lg border border-ring bg-white p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-gray-100 text-ink' : 'text-ink-tertiary hover:text-ink'}`}
              title="Grid view"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-gray-100 text-ink' : 'text-ink-tertiary hover:text-ink'}`}
              title="List view"
            >
              <ListIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          {canJoin && (
            <>
              <Link to={`/events/${eventId}/teams/new`} className="btn-primary text-sm inline-flex items-center gap-1">
                <Plus className="h-3.5 w-3.5" /> Create
              </Link>
              <Link to={`/events/${eventId}/teams/join`} className="btn-secondary text-sm inline-flex items-center gap-1">
                <UserPlus className="h-3.5 w-3.5" /> Join
              </Link>
            </>
          )}
          {canManageTeams && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-secondary text-sm inline-flex items-center gap-1"
            >
              {showCreateForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              {showCreateForm ? 'Cancel' : 'Add Team'}
            </button>
          )}
        </div>
      </div>

      {/* Admin create form */}
      {showCreateForm && canManageTeams && (
        <form onSubmit={handleAdminCreateTeam} className="mb-4 card p-3 border-accent/20">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input type="text" className="input-base flex-1 text-sm" placeholder="Team name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} autoFocus />
            <select className="input-base sm:w-48 text-sm" value={newTeamTheme} onChange={(e) => setNewTeamTheme(e.target.value)}>
              {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button type="submit" className="btn-primary text-sm inline-flex items-center justify-center gap-1" disabled={creating || !newTeamName.trim()}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create
            </button>
          </div>
          {createError && <p className="mt-2 text-xs text-red-600">{createError}</p>}
        </form>
      )}

      {/* No results after filter */}
      {filteredTeams.length === 0 && (
        <div className="card py-10 text-center">
          <Search className="mx-auto h-8 w-8 text-ink-tertiary" />
          <p className="mt-3 text-sm font-medium text-ink">No teams match your search</p>
          <button
            onClick={() => { setQuery(''); setThemeFilter('all'); }}
            className="mt-3 text-xs font-medium text-accent hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Results count */}
      {filteredTeams.length > 0 && (query || themeFilter !== 'all') && (
        <p className="mb-3 text-xs text-ink-tertiary">
          Showing {filteredTeams.length} of {teams.length} team{teams.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Team cards */}
      {filteredTeams.length > 0 && viewMode === 'grid' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTeams.map((team) => {
            const theme = themes.find((t) => t.id === team.themeId);
            const isMine = myTeam?.teamId === team.teamId;
            const photo = (team as { teamPhotoUrl?: string }).teamPhotoUrl;
            return (
              <Link
                key={team.teamId}
                to={`/events/${eventId}/teams/${team.teamId}`}
                className={`group relative flex flex-col overflow-hidden rounded-xl border bg-surface-raised transition-all hover:shadow-card-hover ${isMine ? 'border-blue-300 ring-2 ring-blue-100' : 'border-ring'}`}
              >
                {isMine && (
                  <span className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-blue-500 px-2 py-0.5 text-2xs font-semibold text-white shadow">
                    <CheckIcon className="h-2.5 w-2.5" /> Your team
                  </span>
                )}
                {/* Photo / avatar */}
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  {photo ? (
                    <img src={photo} alt={team.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-amber-400 via-red-400 to-purple-500">
                      <span className="text-3xl font-bold text-white">
                        {team.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="line-clamp-1 font-semibold text-ink">{team.name}</h3>
                  {theme && (
                    <span className={`mt-1.5 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${themeColor(theme.id)}`}>
                      {theme.name}
                    </span>
                  )}

                  {/* Footer: members + actions */}
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <MemberStack members={team.members} />
                    {(can('submission:view-all') || team.members.some((m: { sub: string }) => m.sub === user?.sub)) && (
                      <button
                        onClick={(e) => { e.preventDefault(); window.location.href = `/events/${eventId}/teams/${team.teamId}`; }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-2xs font-medium text-ink-tertiary transition-colors hover:bg-gray-100 hover:text-ink"
                      >
                        <Eye className="h-3 w-3" /> Submission
                      </button>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* List view */}
      {filteredTeams.length > 0 && viewMode === 'list' && (
        <div className="card divide-y divide-ring overflow-hidden">
          {filteredTeams.map((team) => {
            const theme = themes.find((t) => t.id === team.themeId);
            const isMine = myTeam?.teamId === team.teamId;
            const photo = (team as { teamPhotoUrl?: string }).teamPhotoUrl;
            return (
              <Link
                key={team.teamId}
                to={`/events/${eventId}/teams/${team.teamId}`}
                className={`group flex items-center gap-3 p-3 transition-colors hover:bg-surface-sunken ${isMine ? 'bg-blue-50/50' : ''}`}
              >
                {/* Avatar */}
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                  {photo ? (
                    <img src={photo} alt={team.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-amber-400 via-red-400 to-purple-500 text-xs font-bold text-white">
                      {team.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                {/* Name + theme */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-medium text-ink">{team.name}</h3>
                    {isMine && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 px-1.5 py-0 text-2xs font-semibold text-blue-700">
                        <CheckIcon className="h-2.5 w-2.5" /> Yours
                      </span>
                    )}
                  </div>
                  {theme && (
                    <span className={`mt-0.5 inline-flex items-center rounded-full border px-1.5 py-0 text-2xs ${themeColor(theme.id)}`}>
                      {theme.name}
                    </span>
                  )}
                </div>
                {/* Members */}
                <div className="hidden sm:block">
                  <MemberStack members={team.members} compact />
                </div>
                <span className="hidden sm:inline text-xs text-ink-tertiary">
                  {team.members.length} {team.members.length === 1 ? 'member' : 'members'}
                </span>
                <ArrowLeft className="h-4 w-4 shrink-0 rotate-180 text-ink-tertiary transition-transform group-hover:translate-x-0.5" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MemberStack({ members, compact }: { members: Array<{ sub: string; name: string; role: string }>; compact?: boolean }) {
  const visible = members.slice(0, 3);
  const remaining = members.length - visible.length;
  const size = compact ? 'h-6 w-6 text-2xs' : 'h-7 w-7 text-2xs';
  return (
    <div className="flex items-center">
      <div className="flex -space-x-1.5">
        {visible.map((m) => (
          <div
            key={m.sub}
            title={m.name}
            className={`${size} flex items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-amber-400 via-red-400 to-purple-500 font-bold text-white`}
          >
            {m.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <span className={`${size} -ml-1.5 flex items-center justify-center rounded-full border-2 border-white bg-gray-100 font-semibold text-ink-secondary`}>
          +{remaining}
        </span>
      )}
      {!compact && (
        <span className="ml-2 text-xs text-ink-tertiary">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </span>
      )}
    </div>
  );
}

function ScoringTab({ event }: { event: EventDetail }) {
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ring bg-surface-sunken">
            <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Criterion</th>
            <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Weight</th>
            <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Max Score</th>
          </tr>
        </thead>
        <tbody>
          {event.scoringCriteria.map((c) => (
            <tr key={c.name} className="border-b border-ring last:border-0">
              <td className="px-4 py-2.5 text-ink">{c.name}</td>
              <td className="px-4 py-2.5 text-right text-ink-secondary">{c.weight}%</td>
              <td className="px-4 py-2.5 text-right text-ink-secondary">{c.maxScore}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab({ event }: { event: EventDetail }) {
  const [notifMessage, setNotifMessage] = useState('');
  const [notifAudience, setNotifAudience] = useState<'all' | 'unsubmitted' | 'submitted'>('all');
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState<string | null>(null);

  async function handleExport(type: 'submissions' | 'scores' | 'leaderboard' | 'teams') {
    try {
      const res = await networkManager.post<{ status: boolean; data: unknown }>(
        `/api/bulk/export/${type}`,
        { eventId: event.eventId },
      );
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.eventId}-${type}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  }

  async function handleSendNotification() {
    if (!notifMessage.trim()) return;
    setNotifSending(true);
    setNotifResult(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: { recipientTeams: number; recipientUsers: number; emailsSent?: number; emailsFailed?: number } }>(
        '/api/bulk/notifications',
        { eventId: event.eventId, message: notifMessage.trim(), audience: notifAudience },
      );
      const d = res.data;
      setNotifResult(`Emailed ${d.emailsSent ?? 0} users across ${d.recipientTeams} teams${d.emailsFailed ? ` (${d.emailsFailed} failed)` : ''}`);
      setNotifMessage('');
    } catch {
      setNotifResult('Failed to send notification');
    } finally {
      setNotifSending(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-sm text-ink-secondary">Team Size</p>
          <p className="text-lg font-semibold text-ink">{event.settings.minTeamSize}–{event.settings.maxTeamSize}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-ink-secondary">Timer per Team</p>
          <p className="text-lg font-semibold text-ink">{Math.floor(event.settings.timerDurationSeconds / 60)} min</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-ink-secondary">Submission Deadline</p>
          <p className="text-lg font-semibold text-ink">{event.submissionDeadline ? new Date(event.submissionDeadline).toLocaleString() : '—'}</p>
        </div>
      </div>

      {/* Notifications */}
      <div>
        <h3 className="text-sm font-semibold text-ink">Send Notification</h3>
        <p className="mt-1 text-xs text-ink-tertiary">Broadcast a message to teams in this event</p>
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            <select className="input-base w-auto text-sm" value={notifAudience} onChange={(e) => setNotifAudience(e.target.value as typeof notifAudience)}>
              <option value="all">All teams</option>
              <option value="unsubmitted">Unsubmitted only</option>
              <option value="submitted">Submitted only</option>
            </select>
          </div>
          <textarea
            className="input-base text-sm"
            rows={3}
            value={notifMessage}
            onChange={(e) => setNotifMessage(e.target.value)}
            placeholder="Reminder: submissions due in 2 hours!"
            maxLength={1000}
          />
          <div className="flex items-center gap-3">
            <button className="btn-primary text-sm" onClick={handleSendNotification} disabled={notifSending || !notifMessage.trim()}>
              {notifSending ? 'Sending...' : 'Send'}
            </button>
            {notifResult && <span className="text-sm text-ink-secondary">{notifResult}</span>}
          </div>
        </div>
      </div>

      {/* Export section */}
      <div>
        <h3 className="text-sm font-semibold text-ink">Export Data</h3>
        <p className="mt-1 text-xs text-ink-tertiary">Download event data as JSON files</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => handleExport('submissions')} className="btn-secondary text-sm">
            Submissions
          </button>
          <button onClick={() => handleExport('scores')} className="btn-secondary text-sm">
            Scores
          </button>
          <button onClick={() => handleExport('leaderboard')} className="btn-secondary text-sm">
            Leaderboard
          </button>
          <button onClick={() => handleExport('teams')} className="btn-secondary text-sm">
            Teams
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Leaderboard Tab ---------- */

interface LeaderboardEntry {
  rank: number;
  teamId: string;
  teamName: string;
  theme: string;
  averageScore: number;
  judgeCount: number;
  badges: string[];
  productName?: string;
  productSummary?: string;
  feedbackSummary?: {
    text: string;
    generatedAt: string;
    basedOnScoreCount: number;
  };
  teamPhotoUrl?: string;
}

type Visibility = 'hidden' | 'judges_only' | 'public';

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 text-lg">🥇</span>;
  if (rank === 2) return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg">🥈</span>;
  if (rank === 3) return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-lg">🥉</span>;
  return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-sunken text-sm font-semibold text-ink-secondary">{rank}</span>;
}

function LeaderboardTab({ eventId }: { eventId: string }) {
  const { can } = useAuth();
  const canManage = can('leaderboard:manage');
  const canExport = can('bulk:export');
  // Per-judge breakdown is admin-only. Organizers still see the aggregate
  // score, AI feedback summary, and can award badges — they just can't
  // attribute individual scores to named judges.
  const canViewJudgeBreakdown = can('admin');

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<Visibility>('hidden');
  const [publicLimit, setPublicLimit] = useState<number | ''>('');
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [badgeTeamId, setBadgeTeamId] = useState<string | null>(null);
  const [badgeName, setBadgeName] = useState('');
  const [awardingBadge, setAwardingBadge] = useState(false);
  const [exportingLeaderboard, setExportingLeaderboard] = useState(false);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [teamScores, setTeamScores] = useState<Array<{ judgeId: string; criteria: Array<{ name: string; score: number; comment?: string }>; weightedTotal: number }>>([]);
  const [loadingScores, setLoadingScores] = useState(false);

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: LeaderboardEntry[] }>(`/api/events/${eventId}/scores/leaderboard`),
      networkManager.get<{ status: boolean; data: { leaderboardVisibility?: string; publicLeaderboardLimit?: number } }>(`/api/events/${eventId}`).catch(() => ({ data: { leaderboardVisibility: 'hidden' as string | undefined, publicLeaderboardLimit: undefined as number | undefined } })),
    ])
      .then(([lbRes, eventRes]) => {
        setEntries(lbRes.data);
        setVisibility((eventRes.data.leaderboardVisibility as Visibility) ?? 'hidden');
        setPublicLimit(eventRes.data.publicLeaderboardLimit ?? '');
      })
      .catch((err) => setError(getErrorMessage(err, 'Failed to load leaderboard')))
      .finally(() => setLoading(false));
  }, [eventId]);

  async function toggleTeamScores(teamId: string) {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
      return;
    }
    setExpandedTeamId(teamId);
    setLoadingScores(true);
    try {
      const res = await networkManager.get<{ status: boolean; data: Array<{ judgeId: string; criteria: Array<{ name: string; score: number; comment?: string }>; weightedTotal: number }> }>(
        `/api/events/${eventId}/scores/teams/${teamId}`,
      );
      setTeamScores(res.data);
    } catch {
      setTeamScores([]);
    } finally {
      setLoadingScores(false);
    }
  }

  async function handleVisibilityChange(newVis: Visibility) {
    setUpdatingVisibility(true);
    try {
      await networkManager.put(`/api/events/${eventId}/scores/leaderboard`, { visibility: newVis });
      setVisibility(newVis);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update visibility'));
    } finally {
      setUpdatingVisibility(false);
    }
  }

  async function handlePublicLimitChange(raw: number | '') {
    setPublicLimit(raw);
    // Debounce isn't worth it — blur commits. Immediate save on change is fine.
    setUpdatingVisibility(true);
    try {
      await networkManager.put(`/api/events/${eventId}/scores/leaderboard`, {
        visibility,
        publicLimit: raw === '' ? null : Number(raw),
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update public cap'));
    } finally {
      setUpdatingVisibility(false);
    }
  }

  async function handleAwardBadge() {
    if (!badgeTeamId || !badgeName.trim()) return;
    setAwardingBadge(true);
    try {
      await networkManager.post(`/api/events/${eventId}/scores/badges`, { teamId: badgeTeamId, badge: badgeName.trim() });
      setEntries((prev) => prev.map((e) => e.teamId === badgeTeamId ? { ...e, badges: [...e.badges, badgeName.trim()] } : e));
      setBadgeTeamId(null);
      setBadgeName('');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to award badge'));
    } finally {
      setAwardingBadge(false);
    }
  }

  async function handleExportLeaderboard() {
    setExportingLeaderboard(true);
    try {
      const res = await networkManager.post<{ status: boolean; data: { leaderboard: LeaderboardEntry[]; exportedAt: string } }>('/api/bulk/export/leaderboard', { eventId });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leaderboard-${eventId}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to export leaderboard'));
    } finally {
      setExportingLeaderboard(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" /></div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Trophy className="h-7 w-7 text-yellow-500" />
          <h2 className="font-display text-2xl font-bold text-ink">Leaderboard</h2>
        </div>
        {canManage && entries.length > 0 && (
          <a
            href={`/events/${eventId}/leaderboard?ceremony=true`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
          >
            <Trophy className="h-4 w-4" /> Ceremony Mode
          </a>
        )}
      </div>

      {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {/* Visibility toggle */}
      {canManage && (
        <div className="mt-4 card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink">Visibility:</span>
            {([
              { value: 'hidden' as const, label: 'Hidden', icon: EyeOff },
              { value: 'judges_only' as const, label: 'Judges Only', icon: Users },
              { value: 'public' as const, label: 'Public', icon: Eye },
            ]).map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleVisibilityChange(opt.value)}
                disabled={updatingVisibility}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  visibility === opt.value ? 'bg-accent text-white' : 'bg-surface-sunken text-ink-secondary hover:text-ink'
                }`}
              >
                {updatingVisibility ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <opt.icon className="h-3.5 w-3.5" />}
                {opt.label}
              </button>
            ))}
          </div>
          {visibility === 'public' && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ring pt-3">
              <label htmlFor="public-limit" className="text-sm text-ink-secondary">
                Public viewers see top
              </label>
              <input
                id="public-limit"
                type="number"
                min={1}
                max={500}
                placeholder="all"
                value={publicLimit}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setPublicLimit('');
                  else {
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n)) setPublicLimit(n);
                  }
                }}
                onBlur={() => handlePublicLimitChange(publicLimit)}
                className="w-20 rounded-lg border border-ring bg-surface-raised px-2 py-1 text-sm text-ink"
              />
              <span className="text-sm text-ink-secondary">teams.</span>
              <span className="text-xs text-ink-tertiary">
                Leave blank to show all. Judges and organizers always see the full ranking.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Export */}
      {canExport && entries.length > 0 && (
        <div className="mt-4 flex gap-2">
          <button className="btn-secondary inline-flex items-center gap-1.5 text-sm" onClick={handleExportLeaderboard} disabled={exportingLeaderboard}>
            {exportingLeaderboard ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export JSON
          </button>
        </div>
      )}

      {/* Empty state */}
      {!error && entries.length === 0 && (
        <div className="mt-8 py-12 text-center">
          <Trophy className="mx-auto h-12 w-12 text-ink-tertiary" />
          <p className="mt-4 text-ink-secondary">{canManage ? "No scores yet — judging hasn't started" : 'Leaderboard will be revealed after judging'}</p>
        </div>
      )}

      {/* Table - desktop */}
      {entries.length > 0 && (
        <>
          <div className="mt-6 hidden sm:block">
            <div className="card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ring bg-surface-sunken">
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary w-16">Rank</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Team</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Theme</th>
                    <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Badges</th>
                    <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Avg Score</th>
                    <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Judges</th>
                    <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const hasExpansion = !!entry.feedbackSummary || canViewJudgeBreakdown;
                    return (
                    <React.Fragment key={entry.teamId}>
                    <tr className={`border-b border-ring last:border-0 ${hasExpansion ? 'cursor-pointer hover:bg-surface-sunken/50' : ''}`} onClick={() => hasExpansion && toggleTeamScores(entry.teamId)}>
                      <td className="px-4 py-3 align-top"><RankBadge rank={entry.rank} /></td>
                      <td className="px-4 py-3 align-top font-medium text-ink">
                        <Link to={`/events/${eventId}/teams/${entry.teamId}`} className="hover:text-accent transition-colors" onClick={(e) => e.stopPropagation()}>{entry.teamName}</Link>
                        {entry.productName && (
                          <Link
                            to={`/events/${eventId}/teams/${entry.teamId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 block text-xs font-normal text-ink-secondary hover:text-accent transition-colors"
                          >
                            {entry.productName}
                          </Link>
                        )}
                        {entry.productSummary && (
                          <p className="mt-0.5 max-w-md text-xs font-normal italic text-ink-tertiary">{entry.productSummary}</p>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className="badge bg-accent-subtle text-ink">{entry.theme}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {entry.badges.map((badge, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-2xs font-medium text-yellow-800">
                              <Award className="h-3 w-3" /> {badge}
                              {canManage && (
                                <button
                                  className="ml-0.5 hover:text-red-600 transition-colors"
                                  title="Remove badge"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      // Fetch all badges to find the global index
                                      const res = await networkManager.get<{ status: boolean; data: Array<{ teamId: string; badge: string }> }>(`/api/events/${eventId}/scores/badges`);
                                      const globalIdx = res.data.findIndex((b) => b.teamId === entry.teamId && b.badge === badge);
                                      if (globalIdx >= 0) {
                                        await networkManager.delete(`/api/events/${eventId}/scores/badges/${globalIdx}`);
                                        setEntries((prev) => prev.map((en) =>
                                          en.teamId === entry.teamId
                                            ? { ...en, badges: en.badges.filter((_, idx) => idx !== i) }
                                            : en,
                                        ));
                                      }
                                    } catch { /* ignore */ }
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-ink">{entry.averageScore.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-ink-secondary">{entry.judgeCount}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to={`/events/${eventId}/teams/${entry.teamId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="btn-ghost text-xs inline-flex items-center gap-1"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </Link>
                        {canManage && (
                          <button className="btn-ghost ml-1 text-xs inline-flex items-center gap-1" onClick={(e) => { e.stopPropagation(); setBadgeTeamId(entry.teamId); setBadgeName(''); }}>
                            <Award className="h-3.5 w-3.5" /> Award Badge
                          </button>
                        )}
                      </td>
                    </tr>
                    {hasExpansion && expandedTeamId === entry.teamId && (
                      <tr>
                        <td colSpan={7} className="bg-surface-sunken/30 px-4 py-3">
                          {entry.feedbackSummary && (
                            <div className="mb-3 rounded-lg border border-accent/30 bg-accent-subtle/50 p-3">
                              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-secondary">
                                AI feedback summary · {entry.feedbackSummary.basedOnScoreCount} judge{entry.feedbackSummary.basedOnScoreCount === 1 ? '' : 's'}
                              </p>
                              <p className="mt-1 text-sm text-ink">{entry.feedbackSummary.text}</p>
                            </div>
                          )}
                          {canViewJudgeBreakdown ? (
                            loadingScores ? (
                              <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-ink-tertiary" /></div>
                            ) : teamScores.length === 0 ? (
                              <p className="text-sm text-ink-tertiary">No scores submitted yet.</p>
                            ) : (
                              <div className="space-y-3">
                                {teamScores.map((score, si) => (
                                  <div key={si} className="rounded-lg border border-ring bg-white p-3">
                                    <p className="text-xs font-semibold text-ink-secondary">Judge: {score.judgeId}</p>
                                    <div className="mt-1 grid gap-1">
                                      {score.criteria.map((c) => (
                                        <div key={c.name} className="flex items-start gap-2 text-sm">
                                          <span className="font-medium text-ink w-32 shrink-0">{c.name}: {c.score}</span>
                                          {c.comment && <span className="text-ink-secondary italic">"{c.comment}"</span>}
                                        </div>
                                      ))}
                                    </div>
                                    <p className="mt-1 text-xs text-ink-tertiary">Weighted: {score.weightedTotal.toFixed(2)}</p>
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            !entry.feedbackSummary && (
                              <p className="text-sm text-ink-tertiary">No AI summary yet — it’s generated after the first vote comes in.</p>
                            )
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cards - mobile */}
          <div className="mt-6 space-y-3 sm:hidden">
            {entries.map((entry) => (
              <div key={entry.teamId} className="card p-4">
                <Link to={`/events/${eventId}/teams/${entry.teamId}`} className="flex items-start gap-4">
                  <RankBadge rank={entry.rank} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{entry.teamName}</p>
                    {entry.productName && (
                      <p className="truncate text-xs text-ink-secondary">{entry.productName}</p>
                    )}
                    <span className="badge mt-1 bg-accent-subtle text-ink text-xs">{entry.theme}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold text-ink">{entry.averageScore.toFixed(2)}</p>
                    <p className="text-xs text-ink-tertiary">{entry.judgeCount} judge{entry.judgeCount !== 1 ? 's' : ''}</p>
                  </div>
                </Link>
                {entry.productSummary && (
                  <p className="mt-2 text-xs italic text-ink-tertiary">{entry.productSummary}</p>
                )}
                {entry.feedbackSummary && (
                  <div className="mt-2 rounded-lg border border-accent/30 bg-accent-subtle/50 p-2.5">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-ink-secondary">
                      AI feedback · {entry.feedbackSummary.basedOnScoreCount} judge{entry.feedbackSummary.basedOnScoreCount === 1 ? '' : 's'}
                    </p>
                    <p className="mt-1 text-xs text-ink">{entry.feedbackSummary.text}</p>
                  </div>
                )}
                {entry.badges.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.badges.map((badge, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-2xs font-medium text-yellow-800"><Award className="h-3 w-3" /> {badge}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/events/${eventId}/teams/${entry.teamId}`} className="btn-ghost text-xs inline-flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" /> View submission
                  </Link>
                  {canManage && (
                    <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => { setBadgeTeamId(entry.teamId); setBadgeName(''); }}>
                      <Award className="h-3.5 w-3.5" /> Award Badge
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Award Badge Modal */}
      {badgeTeamId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card mx-4 w-full max-w-sm p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">Award Badge</h2>
              <button className="btn-ghost" onClick={() => setBadgeTeamId(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-sm text-ink-secondary">Team: {entries.find((e) => e.teamId === badgeTeamId)?.teamName ?? badgeTeamId}</p>
            <input type="text" className="input-base mt-3" placeholder="Badge name (e.g. Best Design)" value={badgeName} onChange={(e) => setBadgeName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAwardBadge()} />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setBadgeTeamId(null)}>Cancel</button>
              <button className="btn-primary inline-flex items-center gap-1.5" onClick={handleAwardBadge} disabled={awardingBadge || !badgeName.trim()}>
                {awardingBadge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />} Award
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Event Feedback Tab ---------- */

function EventFeedbackTab({ eventId }: { eventId: string; event: EventDetail }) {
  const { can } = useAuth();
  const canViewAll = can('score:view-all');

  const [formConfig, setFormConfig] = useState<{ enabled: boolean; introHtml: string; questions: Array<{ id: string; type: 'text' | 'rating'; label: string; maxRating?: number }> } | null>(null);
  const [mySubmission, setMySubmission] = useState<{ submitted: boolean; answers?: Array<{ questionId: string; value: string | number }> }>({ submitted: false });
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Admin: all responses + analytics
  const [allResponses, setAllResponses] = useState<Array<{ feedbackId: string; userId: string; userName: string; answers: Array<{ questionId: string; value: string | number }>; submittedAt: string }>>([]);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [summarizing, setSummarizing] = useState(false);
  const [showAllResponses, setShowAllResponses] = useState(false);

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: typeof formConfig }>(`/api/events/${eventId}/event-feedback/form`).catch(() => ({ data: null })),
      networkManager.get<{ status: boolean; data: { submitted: boolean; answers?: Array<{ questionId: string; value: string | number }> } }>(`/api/events/${eventId}/event-feedback/mine`).catch(() => ({ data: { submitted: false, answers: undefined } as { submitted: boolean; answers?: Array<{ questionId: string; value: string | number }> } })),
      canViewAll ? networkManager.get<{ status: boolean; data: typeof allResponses }>(`/api/events/${eventId}/event-feedback`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]).then(([formRes, mineRes, allRes]) => {
      setFormConfig(formRes.data);
      setMySubmission(mineRes.data);
      if (allRes.data) setAllResponses(allRes.data as typeof allResponses);
      // Init answers
      if (formRes.data?.questions && !mineRes.data.submitted) {
        const initial: Record<string, string | number> = {};
        formRes.data.questions.forEach((q) => { initial[q.id] = q.type === 'rating' ? 5 : ''; });
        setAnswers(initial);
      }
      if (mineRes.data.submitted && mineRes.data.answers) {
        const loaded: Record<string, string | number> = {};
        mineRes.data.answers.forEach((a: { questionId: string; value: string | number }) => { loaded[a.questionId] = a.value; });
        setAnswers(loaded);
      }
    }).finally(() => setLoading(false));
  }, [eventId, canViewAll]);

  async function handleSubmit() {
    if (!formConfig) return;
    setSubmitting(true);
    setError(null);
    try {
      const answerArray = formConfig.questions.map((q) => ({ questionId: q.id, value: answers[q.id] ?? '' }));
      await networkManager.post(`/api/events/${eventId}/event-feedback`, { answers: answerArray });
      setSuccess(true);
      setMySubmission({ submitted: true, answers: answerArray });
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAiSummarize() {
    if (allResponses.length === 0) return;
    setSummarizing(true);
    try {
      const res = await networkManager.post<{ status: boolean; data: { summary: string; totalResponses: number } }>(
        `/api/events/${eventId}/event-feedback/summarize`, {},
      );
      setAiSummary(res.data.summary);
    } catch {
      setAiSummary('Failed to generate summary. Please try again.');
    } finally {
      setSummarizing(false);
    }
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" /></div>;
  if (!formConfig?.enabled) return <p className="py-12 text-center text-ink-secondary">Feedback form is not enabled for this event.</p>;

  return (
    <div>
      {/* Admin: Analytics + AI Summary + Responses */}
      {canViewAll && allResponses.length > 0 && formConfig && (
        <div className="mb-6 space-y-4">
          {/* Stats overview */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink">Feedback Analytics ({allResponses.length} responses)</h2>
            <button
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              onClick={handleAiSummarize}
              disabled={summarizing}
            >
              {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {summarizing ? 'Summarizing...' : aiSummary ? 'Regenerate Summary' : 'AI Summary'}
            </button>
          </div>

          {/* Rating averages */}
          {formConfig.questions.filter((q) => q.type === 'rating').map((q) => {
            const values = allResponses
              .map((r) => r.answers.find((a) => a.questionId === q.id)?.value)
              .filter((v) => typeof v === 'number') as number[];
            const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
            const max = q.maxRating ?? 10;
            return (
              <div key={q.id} className="card p-4">
                <p className="text-sm font-medium text-ink">{q.label}</p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1 h-3 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${(avg / max) * 100}%` }} />
                  </div>
                  <span className="text-lg font-bold text-ink">{avg.toFixed(1)}</span>
                  <span className="text-sm text-ink-tertiary">/ {max}</span>
                </div>
                <div className="mt-1 flex gap-1">
                  {Array.from({ length: max }, (_, i) => {
                    const count = values.filter((v) => v === i + 1).length;
                    return (
                      <div key={i} className="flex-1 text-center">
                        <div className="mx-auto h-8 w-full rounded bg-gray-100 relative overflow-hidden">
                          <div className="absolute bottom-0 w-full bg-accent/30 transition-all" style={{ height: `${values.length > 0 ? (count / values.length) * 100 : 0}%` }} />
                        </div>
                        <span className="text-2xs text-ink-tertiary">{i + 1}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-ink-tertiary">{values.length} responses</p>
              </div>
            );
          })}

          {/* AI Summary */}
          {aiSummary && (
            <div className="card border-purple-200 bg-purple-50/30 p-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                <Sparkles className="h-4 w-4" /> AI Summary
              </h3>
              <div className="mt-2 whitespace-pre-wrap text-sm text-ink leading-relaxed">{aiSummary}</div>
            </div>
          )}

          {/* Text response summaries per question */}
          {formConfig.questions.filter((q) => q.type === 'text').map((q) => {
            const textAnswers = allResponses
              .map((r) => ({ name: r.userName, text: r.answers.find((a) => a.questionId === q.id)?.value as string }))
              .filter((a) => a.text?.trim());
            return (
              <div key={q.id} className="card p-4">
                <p className="text-sm font-medium text-ink">{q.label}</p>
                <p className="text-xs text-ink-tertiary mt-1">{textAnswers.length} responses</p>
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                  {textAnswers.map((a, i) => (
                    <div key={i} className="rounded-lg bg-surface-sunken p-2">
                      <span className="text-xs font-medium text-ink-secondary">{a.name}</span>
                      <p className="text-sm text-ink mt-0.5 whitespace-pre-wrap">{a.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Individual responses toggle */}
          <button className="btn-ghost text-sm" onClick={() => setShowAllResponses(!showAllResponses)}>
            {showAllResponses ? 'Hide' : 'Show'} individual responses
          </button>
          {showAllResponses && (
            <div className="space-y-3">
              {allResponses.map((r) => (
                <div key={r.feedbackId} className="card p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-ink">{r.userName}</span>
                    <span className="text-xs text-ink-tertiary">{new Date(r.submittedAt).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 space-y-2">
                    {formConfig.questions.map((q) => {
                      const ans = r.answers.find((a) => a.questionId === q.id);
                      return (
                        <div key={q.id}>
                          <p className="text-xs font-semibold text-ink-secondary">{q.label}</p>
                          {q.type === 'rating' ? (
                            <p className="text-sm font-bold text-ink">{ans?.value ?? '-'} / {q.maxRating ?? 10}</p>
                          ) : (
                            <p className="text-sm text-ink whitespace-pre-wrap">{(ans?.value as string) || '-'}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* User form */}
      <div className="mx-auto max-w-2xl">
        {formConfig.introHtml && (
          <div className="card p-4 sm:p-5 mb-4">
            <style>{`.html-content * { margin: 0; padding: 0; } .html-content h1 { font-size: 1.125rem; font-weight: 700; margin-bottom: 0.25rem; } .html-content h2 { font-size: 1rem; font-weight: 600; margin-top: 0.75rem; margin-bottom: 0.25rem; } .html-content h3 { font-size: 0.875rem; font-weight: 600; margin-top: 0.5rem; } .html-content p { margin-bottom: 0.25rem; line-height: 1.4; } .html-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; } .html-content ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25rem 0; } .html-content li { line-height: 1.4; } .html-content .highlight { font-weight: 600; color: #7c3aed; } .html-content strong { font-weight: 600; } .html-content a { color: #7c3aed; text-decoration: underline; } .html-content div { margin-bottom: 0.25rem; }`}</style>
            <div
              className="html-content max-w-none text-sm text-ink"
              dangerouslySetInnerHTML={{ __html: formConfig.introHtml }}
            />
          </div>
        )}

        {mySubmission?.submitted && !canViewAll ? (
          <div className="card p-6 text-center">
            <CheckIcon className="mx-auto h-10 w-10 text-green-500 mb-3" />
            <p className="text-lg font-semibold text-ink">Thank you!</p>
            <p className="mt-1 text-sm text-ink-secondary">Your feedback has been submitted.</p>
          </div>
        ) : mySubmission?.submitted ? (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700 mb-4">
            <CheckIcon className="h-4 w-4" /> You have already submitted feedback.
          </div>
        ) : null}

        {!mySubmission?.submitted && (
          <div className="space-y-4">
            {formConfig.questions.map((q, idx) => (
              <div key={q.id} className="card p-4 sm:p-5">
                <div className="flex items-start gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-bold text-accent">{idx + 1}</span>
                  <label className="text-sm font-semibold text-ink leading-snug">{q.label}</label>
                </div>
                {q.type === 'rating' ? (
                  <div className="mt-3 ml-8">
                    <div className="flex items-center gap-1 flex-wrap">
                      {Array.from({ length: q.maxRating ?? 10 }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setAnswers({ ...answers, [q.id]: n })}
                          className={`h-9 w-9 rounded-lg text-sm font-semibold transition-all ${
                            (answers[q.id] as number) === n
                              ? 'bg-accent text-white shadow-sm scale-110'
                              : 'bg-surface-sunken text-ink-secondary hover:bg-gray-200'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="mt-1 flex justify-between text-2xs text-ink-tertiary">
                      <span>Not at all</span>
                      <span>Extremely well</span>
                    </div>
                  </div>
                ) : (
                  <textarea
                    className="input-base mt-3 ml-8 min-h-[80px] resize-y"
                    value={(answers[q.id] as string) ?? ''}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    placeholder="Your response..."
                    maxLength={5000}
                  />
                )}
              </div>
            ))}

            {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            {success && (
              <div className="card p-6 text-center">
                <CheckIcon className="mx-auto h-10 w-10 text-green-500 mb-3" />
                <p className="text-lg font-semibold text-ink">Thank you!</p>
                <p className="mt-1 text-sm text-ink-secondary">Your feedback has been submitted.</p>
              </div>
            )}

            {!success && (
              <button
                className="btn-primary w-full py-3 text-base inline-flex items-center justify-center gap-2"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageSquare className="h-5 w-5" />}
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

